// La planta de la oficina. Función PURA: tamaño del hueco + salas + contratados → la planta, en «píxeles de arte» (la
// escena amplía por el zoom entero `z`, 3 o 2, nunca menos: nítido).
//
// Es la pantalla de inicio del iPhone (web/qa/auditoria-apple/plan.md, flujo.md #5): cada agente en su mesa FIJA.
//   · Mesas automáticas (compartido/mesas.ts): cada sala, su gente + 1 libre, numeradas en orden de lectura.
//   · Islas de 4 puestos (2×2), todos mirando hacia quien mira: insignia, cabeza, mesa y etiqueta, de arriba abajo.
//   · REJILLA FIJA POR SALA: las columnas de islas salen SOLO del número de mesas de la sala, con tope
//     (`COLUMNAS_PRINCIPAL`, `COLUMNAS_OTRAS`): columnas = min(islas, tope); la isla i va en (i % columnas,
//     ⌊i / columnas⌋). Así la isla i está siempre en el mismo sitio de su sala: el ancho de la ventana o plegar la lista
//     solo cambian el zoom y el desplazamiento, nunca quién se sienta al lado de quién (antes la rejilla se elegía por el
//     ancho, y en el PC «Rehacer…» quedaba a la derecha de «Avisar…» y en el iPad, debajo).
//   · Las salas son superficies rellenas: la principal a la izquierda y las demás en columnas a su derecha (o debajo si
//     no caben a lo ancho). El bloque se centra a lo ancho y se apoya arriba (como los iconos): contratar no lo mueve.
//   · Sin zona común ni recepción (orden.md: no se veían en el PC desde 5 agentes; contratar = «Contratar» y el «+»).
//   · Los que se van no ocupan sitio: franja junto a la puerta, en el zócalo (Salida.tsx); nunca agrandan el mundo.
// Si el bloque no cabe a zoom 2, el mundo crece hacia abajo y se desplaza.
//
// 04-11 — salas = carpetas (orden.md regla 12, plan entrega 3): la principal va siempre desplegada. Si con todas
// desplegadas el bloque no cabe a zoom 2, las secundarias se pliegan en su TARJETA (`CARPETA`, debajo de su cabecera):
// todas menos la que Pau abrió (`abierta`; abrir otra pliega la anterior). Una secundaria sin nadie es siempre su tarjeta
// (con su «+»). La decisión depende del tamaño (`plegado`); DENTRO de una sala nada cambia: su rejilla es la misma
// plegada la vecina o no. 05-11 (#278, CAR-10) — con histéresis: se pliega en cuanto no cabe, pero solo se despliega
// lejos del límite (`HOLGURA_DESPLEGAR`); quien llama pasa la decisión anterior (`plegadasEn`), así que redimensionar
// justo en el límite no pliega y despliega una y otra vez.
//
// 04-11 — grupos de mesas («mesas de equipo», web/qa/auditoria-apple/propuesta-2/fuentes/grupos.md reglas 3-7): en una
// sala CON grupos, cada grupo es un bloque de islas sobre su placa, con su cartel encima, en el orden de Pau, y «Sin
// grupo» va el último (con la única mesa libre). Los bloques fluyen por la misma rejilla de columnas de la sala: uno que
// no cabe en lo que queda de fila empieza fila nueva; uno más ancho que la sala ocupa filas enteras. `mesa` es el sitio
// DENTRO de su grupo. Una sala SIN grupos (ninguno con alguien sentado) se mide y se dibuja exactamente como antes.
import { puestosDeGrupo, totalPuestos } from '../compartido/mesas';

export interface Rect { x: number; y: number; w: number; h: number }
/** El tamaño del hueco de la oficina en pantalla (px). */
export interface Tamano { anchoPx: number; altoPx: number }
export interface Punto { x: number; y: number }

/** Un puesto: 72 de ancho (la etiqueta de 2 líneas cabe en 128 px a zoom 2) por 80 de alto. */
export const CELDA = { w: 72, h: 80 } as const;
export const ISLA = { w: CELDA.w * 2, h: CELDA.h * 2 } as const;
/**
 * Dentro de un puesto, de arriba abajo (píxeles de arte desde su borde de arriba): la insignia ocupa 0-12 (24 px a zoom
 * 2), la cabeza empieza en `cabeza`, la silla en `silla`, la mesa en `mesa` (tablero + frente, `mesaAlto`) y la etiqueta
 * en `etiqueta`, 2 px bajo la mesa (2 líneas: hasta 76).
 */
export const PUESTO = { cabeza: 18, silla: 28, mesa: 40, mesaAncho: 56, mesaAlto: 16, etiqueta: 58 } as const;
const PASILLO_X = 16; // entre islas de una sala
const PASILLO_Y = 12;
/** Entre salas: el doble que entre islas, para que se lea de qué sala es cada isla (visual.md #10). */
const HUECO_SALAS = 2 * PASILLO_X;
const RELLENO = 8; // superficie de la sala alrededor de sus islas
export const ALTO_LETRERO = 20; // la cabecera de la sala, encima de su superficie
/** Grupos: la banda del cartel encima de las islas de un grupo (nombre, número, «⋯»): 32 px a zoom 2. */
export const ALTO_CARTEL = 16;
/** Grupos: el borde de la placa alrededor de sus islas (8 px a zoom 2, «8 entre mesas»). */
export const BORDE_PLACA = 4;
/** Grupos: el aire entre dos placas, a lo ancho y a lo alto (32 px a zoom 2, grupos.md regla 5). */
const AIRE_GRUPOS = 16;
/** Banda lisa de arriba: los widgets SON la pared (visual.md #3); el suelo empieza 16 px debajo de ellos. */
export const PARED = 8;
export const ZOCALO = 14; // pared de abajo, con la puerta y la franja de los que se van
const AIRES = [16, 8, 4] as const; // entre el bloque y los bordes del área visible, de más a menos
/**
 * Tope de columnas de islas: la principal, 2; las demás, 1. Con eso, en 1440×900 caben a la vista 24 mesas (Venuz 2×2
 * islas + Oficina 1×2: generar.test.ts). Más mesas crecen hacia abajo, nunca a lo ancho.
 */
export const COLUMNAS_PRINCIPAL = 2;
export const COLUMNAS_OTRAS = 1;
/**
 * La tarjeta de una sala plegada (carpeta), casi cuadrada como una carpeta de iOS: 224×160 px a z 2 (cabe la rejilla de
 * 3×3 caras y «N agentes»; y su cabecera aún lee el nombre).
 */
export const CARPETA = { w: 112, h: CELDA.h } as const;
/**
 * 05-11 (#278, CAR-10) — la histéresis de las carpetas, en píxeles de pantalla (48 = 24 de arte a zoom 2, más que el
 * temblor de una mano al arrastrar el borde). Plegadas, solo se despliegan si todo desplegado cabe Y, además, la ventana
 * ha crecido esto (a lo ancho o a lo alto) desde que se plegaron, o todo seguiría cabiendo con la ventana esto más
 * estrecha y, aparte, esto más baja (sobra sitio: p. ej. se fue gente). Plegar no la usa: en cuanto no cabe, se pliega.
 * Las dos salidas hacen falta: con el ancho justo (la distribución que cabe no tiene holgura a lo ancho) la segunda sola
 * no desplegaría nunca al crecer solo a lo alto; y sin la segunda, despedir a media oficina no desplegaría hasta mover la
 * ventana.
 */
export const HOLGURA_DESPLEGAR_PX = 48;
/** Cuántos de los que se van se ven en la franja de la puerta; el resto, «+N». */
export const SALIDA_VISIBLES = 3;

export interface SalaMundo {
  projectId: string;
  nombre: string;
  color: string;
  principal: boolean;
}
export interface ContratadoMundo { agentId: string; projectId: string; mesa: number; grupoId?: string | null }
/** 04-11 — un grupo de mesas de una sala (GrupoDTO): se pinta si tiene a alguien sentado. */
export interface GrupoEntrada { id: string; projectId: string; nombre: string; orden: number }

/**
 * Una sala: su superficie (`rect`) y, encima, su cabecera (`letrero`: esquina de arriba a la izquierda). Plegada, `rect` es
 * su tarjeta de carpeta y no tiene mesas. `abierta`: una secundaria que Pau desplegó mientras las demás van plegadas.
 */
export interface ZonaMundo { projectId: string; nombre: string; color: string; rect: Rect; letrero: Punto; plegada?: boolean; abierta?: boolean }

export interface MesaMundo {
  projectId: string;
  /** 04-11 — el grupo de la mesa (null = «Sin grupo», o una sala sin grupos). */
  grupoId: string | null;
  /** La mesa dentro de su grupo (`ContratadoDTO.mesa`). */
  indice: number;
  /** Celda del puesto (para la capa DOM: pulsar, etiqueta). Dentro, `PUESTO` dice dónde va cada cosa. */
  rect: Rect;
  agentId: string | null;
  /** La primera libre de la sala: el «+» para contratar ahí. */
  libre: boolean;
}

/**
 * 04-11 — un bloque de una sala con grupos: un grupo con nombre (`grupoId`) o «Sin grupo» (null). `placa` = el rectángulo
 * que ocupa (se dibuja solo en los grupos con nombre; también es donde se suelta a alguien para meterlo); `cartel` = la
 * banda de arriba, donde la capa DOM pone su nombre y su menú.
 */
export interface GrupoMundo {
  projectId: string;
  grupoId: string | null;
  nombre: string;
  /** Cuántos tiene sentados. */
  n: number;
  placa: Rect;
  cartel: Rect;
}

export interface Mundo {
  z: number;
  ancho: number;
  alto: number;
  /** Área visible (a la derecha de lo que reserva la lista), en píxeles de arte. */
  visible: Rect;
  zonas: ZonaMundo[];
  islas: Rect[];
  mesas: MesaMundo[];
  /** 04-11 — los bloques de las salas con grupos (vacío si ninguna tiene). */
  grupos: GrupoMundo[];
  /** La puerta, en el zócalo: x de su centro, y del borde de arriba del zócalo. */
  puerta: Punto;
  /**
   * 05-11 (#278) — las secundarias van en carpetas porque no cabía todo (`plegado`): el tamaño de la ventana cuando se
   * plegaron (null = desplegadas). Quien genera la planta siguiente se lo devuelve como `plegadasEn` (la histéresis).
   */
  plegadasEn: Tamano | null;
  /**
   * 29-10 (Pau: «cuando un agente se esté yendo, que se ponga en un sitio separado… de fondo»): los que se van no ocupan
   * mesa. Desde el 04-11 (orden.md regla 8) van en una franja junto a la puerta: como mucho `SALIDA_VISIBLES` y «+N».
   */
  salida: { visibles: string[]; mas: number };
  /** El rectángulo de la oficina ocupada (para encuadrar la cámara). */
  encuadre: Rect;
  /**
   * El bloque de la oficina NO cabe en la ventana y hay que desplazarse. Si es false, el mundo solo pasa
   * de la ventana por el redondeo del zoom (hasta z-1 px): eso se recorta, nunca se desplaza — una barra de
   * desplazamiento por 1 px cambia el hueco medido y el mundo entraba en bucle (parpadeo, 28-09).
   */
  desborda: boolean;
}

export interface EntradaMundo {
  anchoPx: number;
  altoPx: number;
  /** Lo que reserva a la izquierda la lista flotante (con sus márgenes), en píxeles de pantalla. */
  panelIzqPx: number;
  salas: SalaMundo[];
  contratados: ContratadoMundo[];
  /** 29-10 — los que se van (sin mesa), en este orden. */
  saliendo?: string[];
  /** 04-11 — los grupos de mesas de las salas (los vacíos no se pintan). */
  grupos?: GrupoEntrada[];
  /** 04-11 — la sala secundaria que Pau abrió como carpeta (solo cuenta si las carpetas están plegadas). */
  abierta?: string | null;
  /**
   * 05-11 (#278) — la decisión anterior (`Mundo.plegadasEn`): si ya iban en carpetas, solo se despliegan lejos del límite
   * (`HOLGURA_DESPLEGAR_PX`). Sin ella (la primera planta), se decide solo por el tamaño.
   */
  plegadasEn?: Tamano | null;
}

/** Un bloque de una sala con grupos, ya colocado en su rejilla: sus islas (celda, puestos). */
interface Bloque { grupoId: string | null; nombre: string; n: number; puestos: number; ocupadas: Map<number, string>; celdas: Array<{ col: number; fila: number; puestos: number }> }
/** La rejilla de una sala con grupos: los bloques y, por fila, si lleva cartel, su alto y dónde empieza (desde la sala). */
interface Rejilla { bloques: Bloque[]; filas: Array<{ cartel: boolean; alto: number; y: number }> }

/** Una sala ya medida: sus puestos, sus islas y su rejilla FIJA (columnas × filas). Con grupos, `rejilla`; plegada, su tarjeta. */
interface PlanoSala { sala: SalaMundo; puestos: number; islas: number; cols: number; w: number; h: number; rejilla?: Rejilla; plegada?: boolean }
interface Colocada { p: PlanoSala; x: number; y: number }
interface Plano { salas: Colocada[]; w: number; h: number }

/** Ancho de una celda de una sala con grupos (la isla y el borde de su placa) y paso entre columnas. */
const CELDA_GRUPO_W = ISLA.w + 2 * BORDE_PLACA;
const PASO_GRUPO_X = CELDA_GRUPO_W + AIRE_GRUPOS;

/**
 * Mide una sala con grupos (ver la cabecera): los bloques en orden (grupos de Pau → «Sin grupo»), cada uno con ⌈n/4⌉
 * islas por la rejilla de columnas de la sala. Un bloque empieza fila nueva si no cabe en lo que queda de la fila o si el
 * anterior ocupaba filas enteras. Cada fila donde empieza un bloque lleva la banda de su cartel.
 */
function medirSalaConGrupos(sala: SalaMundo, grupos: readonly GrupoEntrada[], gente: readonly ContratadoMundo[]): PlanoSala {
  const bloque = (grupoId: string | null, nombre: string): Bloque => {
    const suyos = gente.filter((c) => (c.grupoId ?? null) === grupoId);
    const mesas = suyos.map((c) => c.mesa);
    return { grupoId, nombre, n: suyos.length, puestos: grupoId ? puestosDeGrupo(mesas) : totalPuestos(mesas), ocupadas: new Map(suyos.map((c) => [c.mesa, c.agentId])), celdas: [] };
  };
  const bloques = [...grupos.map((g) => bloque(g.id, g.nombre)), bloque(null, '')];
  const islasDe = (b: Bloque) => Math.ceil(b.puestos / 4);
  const total = bloques.reduce((t, b) => t + islasDe(b), 0);
  const cols = Math.min(total, sala.principal ? COLUMNAS_PRINCIPAL : COLUMNAS_OTRAS);
  let col = 0;
  let fila = 0;
  let anteriorAncho = false;
  const empiezan = new Set<number>();
  for (const b of bloques) {
    const k = islasDe(b);
    if (col > 0 && (col + k > cols || anteriorAncho)) {
      fila += 1;
      col = 0;
    }
    empiezan.add(fila);
    for (let i = 0; i < k; i += 1) {
      if (col === cols) {
        col = 0;
        fila += 1;
      }
      b.celdas.push({ col, fila, puestos: Math.min(4, b.puestos - i * 4) });
      col += 1;
    }
    anteriorAncho = k > cols;
  }
  const nFilas = Math.max(...bloques.flatMap((b) => b.celdas.map((c) => c.fila))) + 1;
  const filas: Rejilla['filas'] = [];
  let y = ALTO_LETRERO + RELLENO;
  for (let f = 0; f < nFilas; f += 1) {
    const enFila = bloques.flatMap((b) => b.celdas.filter((c) => c.fila === f));
    const isla = Math.max(...enFila.map((c) => (c.puestos <= 2 ? CELDA.h : ISLA.h)));
    const cartel = empiezan.has(f);
    const alto = (cartel ? ALTO_CARTEL : BORDE_PLACA) + isla + BORDE_PLACA;
    filas.push({ cartel, alto, y });
    y += alto + AIRE_GRUPOS;
  }
  return {
    sala,
    puestos: bloques.reduce((t, b) => t + b.puestos, 0),
    islas: total,
    cols,
    w: cols * CELDA_GRUPO_W + (cols - 1) * AIRE_GRUPOS + 2 * RELLENO,
    h: y - AIRE_GRUPOS + RELLENO,
    rejilla: { bloques, filas },
  };
}

/** Mide una sala: su rejilla depende solo de cuántas mesas tiene (ver la cabecera). */
function medirSala(sala: SalaMundo, ocupadas: readonly number[]): PlanoSala {
  const puestos = totalPuestos(ocupadas);
  const islas = Math.ceil(puestos / 4);
  const cols = Math.min(islas, sala.principal ? COLUMNAS_PRINCIPAL : COLUMNAS_OTRAS);
  const filas = Math.ceil(islas / cols);
  // La última isla es de media altura si lleva 1-2 puestos; la fila entera, solo si esa isla va sola en ella.
  const ultimaIslaMedia = puestos - (islas - 1) * 4 <= 2;
  const solaEnSuFila = (islas - 1) % cols === 0;
  return {
    sala,
    puestos,
    islas,
    cols,
    w: cols * ISLA.w + (cols - 1) * PASILLO_X + 2 * RELLENO,
    h: ALTO_LETRERO + 2 * RELLENO + filas * ISLA.h - (ultimaIslaMedia && solaEnSuFila ? CELDA.h : 0) + (filas - 1) * PASILLO_Y,
  };
}

/**
 * Las distribuciones posibles del bloque: la principal a la izquierda y las demás repartidas en 0..N columnas a su
 * derecha (0 = todas apiladas debajo). La rejilla de cada sala es la misma en todas.
 */
const ordenarSalas = (salas: readonly SalaMundo[]) => [...salas].sort((a, b) => Number(b.principal) - Number(a.principal) || a.nombre.localeCompare(b.nombre));

function candidatos(e: EntradaMundo, plegadas: ReadonlySet<string>): Plano[] {
  const ordenadas = ordenarSalas(e.salas);
  if (!ordenadas.length) return [{ salas: [], w: 0, h: 0 }];
  const [p, ...resto] = ordenadas.map((s): PlanoSala => {
    if (plegadas.has(s.projectId)) return { sala: s, puestos: 0, islas: 0, cols: 0, w: CARPETA.w, h: ALTO_LETRERO + CARPETA.h, plegada: true };
    const gente = e.contratados.filter((c) => c.projectId === s.projectId);
    // Solo los grupos con alguien sentado (un grupo vacío desaparece, regla 7); sin ninguno, la sala de siempre (regla 3).
    const grupos = (e.grupos ?? [])
      .filter((g) => g.projectId === s.projectId && gente.some((c) => c.grupoId === g.id))
      .sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre));
    return grupos.length ? medirSalaConGrupos(s, grupos, gente) : medirSala(s, gente.map((c) => c.mesa));
  });
  const planos: Plano[] = [];
  for (let columnas = 0; columnas <= resto.length; columnas += 1) {
    const salas: Colocada[] = [{ p: p!, x: 0, y: 0 }];
    if (columnas === 0) {
      let h = p!.h;
      for (const r of resto) {
        salas.push({ p: r, x: 0, y: h + HUECO_SALAS });
        h += HUECO_SALAS + r.h;
      }
      planos.push({ salas, w: Math.max(p!.w, ...resto.map((r) => r.w)), h });
      continue;
    }
    // Columnas a la derecha de la principal: cada sala, en su orden, a la columna más baja (equilibrio).
    const cols = Array.from({ length: columnas }, () => ({ salas: [] as PlanoSala[], w: 0, h: 0 }));
    for (const r of resto) {
      const baja = cols.reduce((a, b) => (b.h < a.h ? b : a));
      baja.h += (baja.salas.length ? HUECO_SALAS : 0) + r.h;
      baja.w = Math.max(baja.w, r.w);
      baja.salas.push(r);
    }
    if (cols.some((c) => !c.salas.length)) continue;
    let x = p!.w;
    for (const c of cols) {
      x += HUECO_SALAS;
      let y = 0;
      for (const r of c.salas) {
        salas.push({ p: r, x, y });
        y += r.h + HUECO_SALAS;
      }
      x += c.w;
    }
    planos.push({ salas, w: x, h: Math.max(p!.h, ...cols.map((c) => c.h)) });
  }
  return planos;
}

/** Lo visible en píxeles de arte a un zoom y con un aire. */
const vista = (e: EntradaMundo, z: number, aire: number) => ({ w: (e.anchoPx - e.panelIzqPx) / z - 2 * aire, h: e.altoPx / z - PARED - ZOCALO - 2 * aire });

/**
 * ¿Alguna distribución cabe entera a zoom 2 (el mínimo) con el menor aire? = no hay que desplazarse. Con `menosPx`, en
 * una ventana así de más estrecha y, aparte, así de más baja.
 */
function cabeAZoom2(e: EntradaMundo, lista: readonly Plano[], menosPx = 0): boolean {
  const cabe = (t: Tamano) => {
    const v = vista({ ...e, ...t }, 2, AIRES[AIRES.length - 1]!);
    return lista.some((p) => p.w <= v.w && p.h <= v.h);
  };
  if (!menosPx) return cabe(e);
  return cabe({ anchoPx: e.anchoPx - menosPx, altoPx: e.altoPx }) && cabe({ anchoPx: e.anchoPx, altoPx: e.altoPx - menosPx });
}

/** Plegadas en `en`: ¿se pueden desplegar ya? Cabe todo y la ventana creció la holgura, o sobra la holgura (ver arriba). */
function despliegaLejosDelLimite(e: EntradaMundo, lista: readonly Plano[], en: Tamano): boolean {
  if (!cabeAZoom2(e, lista)) return false;
  const crecio = e.anchoPx >= en.anchoPx + HOLGURA_DESPLEGAR_PX || e.altoPx >= en.altoPx + HOLGURA_DESPLEGAR_PX;
  return crecio || cabeAZoom2(e, lista, HOLGURA_DESPLEGAR_PX);
}

/**
 * La decisión de las carpetas (orden.md regla 12), PURA: qué salas van plegadas y cuál está abierta. La principal nunca
 * se pliega. Una secundaria sin nadie, siempre (solo su tarjeta con «+»). Si con las demás desplegadas el bloque cabe a
 * zoom 2, no se pliega nada más; si no, se pliegan todas las secundarias menos `e.abierta` (que entonces va `abierta`) y
 * `plegadasEn` dice desde qué tamaño. Histéresis (#278, CAR-10): si ya iban en carpetas (`e.plegadasEn`), desplegar pide
 * estar lejos del límite (`HOLGURA_DESPLEGAR_PX`); plegar, no.
 */
export function plegado(e: EntradaMundo): { plegadas: ReadonlySet<string>; abierta: string | null; plegadasEn: Tamano | null } {
  const [, ...secundarias] = ordenarSalas(e.salas);
  const conGente = new Set(e.contratados.map((c) => c.projectId));
  const vacias = new Set(secundarias.filter((s) => !conGente.has(s.projectId)).map((s) => s.projectId));
  const desplegadas = candidatos(e, vacias);
  if (e.plegadasEn ? despliegaLejosDelLimite(e, desplegadas, e.plegadasEn) : cabeAZoom2(e, desplegadas)) return { plegadas: vacias, abierta: null, plegadasEn: null };
  const abierta = secundarias.some((s) => s.projectId === e.abierta && !vacias.has(s.projectId)) ? e.abierta! : null;
  return { plegadas: new Set(secundarias.map((s) => s.projectId).filter((id) => id !== abierta)), abierta, plegadasEn: e.plegadasEn ?? { anchoPx: e.anchoPx, altoPx: e.altoPx } };
}

/** Cuánto se parece la forma del bloque a la del área visible (0 = igual): desempata entre las que caben. */
const desajuste = (p: Plano, visW: number, visH: number) => Math.abs(Math.log(Math.max(1, p.w) / Math.max(1, p.h) / (visW / Math.max(1, visH))));

export function generarMundo(e: EntradaMundo): Mundo {
  const { plegadas, abierta, plegadasEn } = plegado(e);
  const lista = candidatos(e, plegadas);
  // El mayor zoom (3 o 2) con el que el bloque cabe entero en el área visible; a igual zoom, más aire alrededor y, a
  // igual aire, la forma que mejor llena la pantalla.
  let elegido: { z: number; plano: Plano; aire: number } | null = null;
  for (const z of [3, 2]) {
    for (const aire of AIRES) {
      const { w: visW, h: visH } = vista(e, z, aire);
      const caben = lista.filter((p) => p.w <= visW && p.h <= visH).sort((a, b) => desajuste(a, visW, visH) - desajuste(b, visW, visH));
      if (caben.length) {
        elegido = { z, plano: caben[0]!, aire };
        break;
      }
    }
    if (elegido) break;
  }
  // No cabe a zoom 2: el mundo crece y se desplaza, mejor solo hacia abajo: de lo que cabe a lo ancho, el menos alto.
  // Si nada cabe a lo ancho, el que menos se sale en proporción.
  if (!elegido) {
    const aire = AIRES[AIRES.length - 1]!;
    const { w: visW, h: visH } = vista(e, 2, aire);
    const exceso = (p: Plano) => Math.max(p.w / visW, p.h / visH);
    const plano = [...lista].filter((p) => p.w <= visW).sort((a, b) => a.h - b.h)[0] ?? [...lista].sort((a, b) => exceso(a) - exceso(b))[0]!;
    elegido = { z: 2, plano, aire };
  }
  const { z, plano, aire } = elegido;

  const reservaIzq = Math.ceil(e.panelIzqPx / z);
  // El mundo cubre la ventana; si el bloque no cabe, crece (y la escena se desplaza).
  const ancho = Math.max(Math.ceil(e.anchoPx / z), reservaIzq + plano.w + 2 * aire);
  const alto = Math.max(Math.ceil(e.altoPx / z), PARED + ZOCALO + plano.h + 2 * aire);
  const x0 = Math.round(reservaIzq + (ancho - reservaIzq - plano.w) / 2);
  const y0 = PARED + aire;
  const visible: Rect = { x: reservaIzq, y: PARED, w: ancho - reservaIzq, h: alto - PARED - ZOCALO };

  const zonas: ZonaMundo[] = [];
  const islas: Rect[] = [];
  const mesas: MesaMundo[] = [];
  const grupos: GrupoMundo[] = [];
  for (const { p, x: px, y: py } of plano.salas) {
    const zx = x0 + px;
    const y = y0 + py;
    const zona: ZonaMundo = { projectId: p.sala.projectId, nombre: p.sala.nombre, color: p.sala.color, rect: { x: zx, y: y + ALTO_LETRERO, w: p.w, h: p.h - ALTO_LETRERO }, letrero: { x: zx, y } };
    if (p.plegada) zona.plegada = true;
    else if (p.sala.projectId === abierta) zona.abierta = true;
    zonas.push(zona);
    if (p.plegada) continue;
    if (p.rejilla) {
      colocarConGrupos(p.sala.projectId, p.rejilla, zx, y, islas, mesas, grupos);
      continue;
    }
    const ocupadas = new Map(e.contratados.filter((c) => c.projectId === p.sala.projectId).map((c) => [c.mesa, c.agentId]));
    let primeraLibre = -1;
    for (let i = 0; i < p.islas; i += 1) {
      const enIsla = Math.min(4, p.puestos - i * 4);
      const isla: Rect = {
        x: zx + RELLENO + (i % p.cols) * (ISLA.w + PASILLO_X),
        y: y + ALTO_LETRERO + RELLENO + Math.floor(i / p.cols) * (ISLA.h + PASILLO_Y),
        w: ISLA.w,
        h: enIsla <= 2 ? CELDA.h : ISLA.h,
      };
      islas.push(isla);
      // Orden de lectura dentro de la isla: arriba izq., arriba der., abajo izq., abajo der.
      for (let k = 0; k < enIsla; k += 1) {
        const indice = i * 4 + k;
        const agentId = ocupadas.get(indice) ?? null;
        if (!agentId && primeraLibre < 0) primeraLibre = indice;
        const rect: Rect = { x: isla.x + (k % 2) * CELDA.w, y: isla.y + Math.floor(k / 2) * CELDA.h, w: CELDA.w, h: CELDA.h };
        mesas.push({ projectId: p.sala.projectId, grupoId: null, indice, rect, agentId, libre: !agentId && primeraLibre === indice });
      }
    }
  }

  // La puerta, en el zócalo, junto al borde derecho de la oficina; la franja de los que se van, a su izquierda.
  const puerta: Punto = { x: Math.min(x0 + plano.w - 16, ancho - 24), y: alto - ZOCALO };
  const saliendo = e.saliendo ?? [];
  const salida = { visibles: saliendo.slice(0, SALIDA_VISIBLES), mas: Math.max(0, saliendo.length - SALIDA_VISIBLES) };

  const encuadre: Rect = { x: x0 - aire, y: y0 - aire, w: plano.w + 2 * aire, h: plano.h + 2 * aire };
  const desborda = ancho > Math.ceil(e.anchoPx / z) || alto > Math.ceil(e.altoPx / z);
  return { z, ancho, alto, visible, zonas, islas, mesas, grupos, puerta, plegadasEn, salida, encuadre, desborda };
}

/** Coloca los bloques de una sala con grupos (ya medida) a partir de la esquina de su sala (`zx`, `y`). */
function colocarConGrupos(projectId: string, r: Rejilla, zx: number, y: number, islas: Rect[], mesas: MesaMundo[], grupos: GrupoMundo[]): void {
  const colX = (col: number) => zx + RELLENO + col * PASO_GRUPO_X;
  for (const b of r.bloques) {
    let primeraLibre = -1;
    b.celdas.forEach((celda, i) => {
      const fila = r.filas[celda.fila]!;
      const isla: Rect = {
        x: colX(celda.col) + BORDE_PLACA,
        y: y + fila.y + (fila.cartel ? ALTO_CARTEL : BORDE_PLACA),
        w: ISLA.w,
        h: celda.puestos <= 2 ? CELDA.h : ISLA.h,
      };
      islas.push(isla);
      for (let k = 0; k < celda.puestos; k += 1) {
        const indice = i * 4 + k;
        const agentId = b.ocupadas.get(indice) ?? null;
        // Solo «Sin grupo» lleva la mesa libre con su «+» (regla 4); en un grupo, un sitio vacío es solo la silueta.
        if (!agentId && b.grupoId === null && primeraLibre < 0) primeraLibre = indice;
        const rect: Rect = { x: isla.x + (k % 2) * CELDA.w, y: isla.y + Math.floor(k / 2) * CELDA.h, w: CELDA.w, h: CELDA.h };
        mesas.push({ projectId, grupoId: b.grupoId, indice, rect, agentId, libre: !agentId && b.grupoId === null && primeraLibre === indice });
      }
    });
    const primera = b.celdas[0]!;
    const ultima = b.celdas[b.celdas.length - 1]!;
    const finPrimeraFila = Math.max(...b.celdas.filter((c) => c.fila === primera.fila).map((c) => c.col));
    const finTodas = Math.max(...b.celdas.map((c) => c.col));
    const x = colX(primera.col);
    const top = y + r.filas[primera.fila]!.y;
    const filaFin = r.filas[ultima.fila]!;
    grupos.push({
      projectId,
      grupoId: b.grupoId,
      nombre: b.nombre,
      n: b.n,
      placa: { x, y: top, w: colX(finTodas) + CELDA_GRUPO_W - x, h: y + filaFin.y + filaFin.alto - top },
      cartel: { x, y: top, w: colX(finPrimeraFila) + CELDA_GRUPO_W - x, h: ALTO_CARTEL },
    });
  }
}
